import { useState, useEffect, useCallback } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const parseLocationNames = (result, originalTypes) => {
  if (!result) {
    return { name: "", longName: "" };
  }

  const addressComponents = result.address_components || [];

  // Obtener el tipo de resultado principal basado en types
  const isCountry = originalTypes.includes("country");
  const isRegion = originalTypes.includes("administrative_area_level_1");
  const isSubRegion = originalTypes.includes("administrative_area_level_2");

  // search city (or sublocality) in the address_components:
  const cityComponent =
    addressComponents.find((comp) => comp.types.includes("locality")) ||
    addressComponents.find((comp) => comp.types.includes("sublocality")) ||
    addressComponents.find((comp) =>
      comp.types.includes("administrative_area_level_3")
    ) ||
    addressComponents.find((comp) =>
      comp.types.includes("administrative_area_level_2")
    );

  const city = cityComponent
    ? cityComponent.long_name || cityComponent.short_name
    : "";

  // search region/state in the address_components:
  const regionComponent = addressComponents.find((comp) =>
    comp.types.includes("administrative_area_level_1")
  );
  const region = regionComponent
    ? regionComponent.long_name || regionComponent.short_name
    : "";

  // search country in the address_components:
  const countryComponent = addressComponents.find((comp) =>
    comp.types.includes("country")
  );
  const country = countryComponent
    ? countryComponent.long_name || countryComponent.short_name
    : "";

  // Determinamos el nombre según el tipo de resultado principal
  let name;

  if (isCountry) {
    name = country;
  } else if (isRegion) {
    name = country ? `${region}, ${country}` : region;
  } else if (isSubRegion) {
    const subRegion = addressComponents.find((comp) =>
      comp.types.includes("administrative_area_level_2")
    );
    name = country
      ? `${subRegion?.long_name || ""}, ${country}`
      : subRegion?.long_name || "";
  } else if (city) {
    name = country
      ? `${city}, ${country}`
      : region
        ? `${city}, ${region}`
        : city;
  } else if (region) {
    name = country ? `${region}, ${country}` : region;
  } else {
    name = country;
  }

  // "longName" => concatena city, region and country, omitting the empty ones:
  const parts = [];

  // Si es un país, solo incluimos el país
  if (isCountry) {
    parts.push(country);
  }
  // Si es una región, incluimos región y país
  else if (isRegion) {
    if (region) parts.push(region);
    if (country) parts.push(country);
  }
  // En otros casos, seguimos el comportamiento normal
  else {
    if (city) parts.push(city);
    if (region) parts.push(region);
    if (country) parts.push(country);
  }

  const longName = parts.join(", ");

  return { name, longName };
};

const getLocationTextByLocationObject = (location = {}) => {
  let locationText = "";
  if (location.type === "city") {
    locationText = location.city;
  }
  if (location.type === "region") {
    locationText = location.region;
  }
  locationText += " " + location.country;

  return locationText;
};

const getBestLocation = ({ location, results = [] }) => {
  if (!location || !results.length) {
    return null;
  }

  // Function to normalize text: remove accents, convert to lowercase, remove extra spaces
  const normalizeText = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove accents
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .trim();
  };

  // Get the primary search term based on location type
  const getPrimarySearchTerm = (loc) => {
    if (loc.type === "city" && loc.city) return normalizeText(loc.city);
    if (loc.type === "region" && loc.region) return normalizeText(loc.region);
    if (loc.type === "country" && loc.country)
      return normalizeText(loc.country);
    // Fallback: use first available term
    return normalizeText(loc.city || loc.region || loc.country || "");
  };

  // Get expected Google Places types based on location type
  const getExpectedTypes = (locationType) => {
    switch (locationType) {
      case "city":
        return ["locality", "political", "geocode"];
      case "region":
        return ["administrative_area_level_1", "political", "geocode"];
      case "country":
        return ["country", "political", "geocode"];
      default:
        return ["locality", "political", "geocode"];
    }
  };

  // Types that should be penalized (establishments, routes, etc.)
  const penalizedTypes = [
    "establishment",
    "point_of_interest",
    "route",
    "airport",
    "hospital",
    "store",
    "food",
    "lodging",
    "transit_station",
    "bus_station",
    "train_station",
    "subway_station",
  ];

  const primaryTerm = getPrimarySearchTerm(location);
  const countryTerm = normalizeText(location.country || "");
  const expectedTypes = getExpectedTypes(location.type);

  let bestMatch = null;
  let bestScore = -Infinity;

  results.forEach((result, index) => {
    let score = 0;
    const types = result.types || [];
    const mainText = normalizeText(
      result.structured_formatting?.main_text || ""
    );
    const firstTermValue = normalizeText(result.terms?.[0]?.value || "");

    // === PRIORITY 1: Exact match on main_text (highest priority) ===
    // If main_text exactly matches the primary search term, this is likely the best result
    if (mainText === primaryTerm) {
      score += 100;
    }
    // If first term exactly matches the primary search term
    else if (firstTermValue === primaryTerm) {
      score += 90;
    }
    // If main_text starts with or equals the primary term
    else if (
      mainText.startsWith(primaryTerm + ",") ||
      mainText.startsWith(primaryTerm + " ")
    ) {
      score += 70;
    }
    // If main_text contains the primary term (but not as the main subject)
    else if (mainText.includes(primaryTerm)) {
      score += 30;
    }

    // === PRIORITY 2: Type matching ===
    // Strong bonus for matching expected types
    const hasExpectedType = expectedTypes.some((t) => types.includes(t));
    if (hasExpectedType) {
      // Specific type bonuses
      if (location.type === "city" && types.includes("locality")) {
        score += 50;
      } else if (
        location.type === "region" &&
        types.includes("administrative_area_level_1")
      ) {
        score += 50;
      } else if (location.type === "country" && types.includes("country")) {
        score += 50;
      } else {
        score += 20; // Generic expected type bonus
      }
    }

    // Heavy penalty for establishment/point of interest types
    const hasPenalizedType = penalizedTypes.some((t) => types.includes(t));
    if (hasPenalizedType) {
      score -= 80;
    }

    // === PRIORITY 3: Country verification ===
    // Check if country appears in the result (for validation)
    if (countryTerm) {
      const description = normalizeText(result.description || "");
      const secondaryText = normalizeText(
        result.structured_formatting?.secondary_text || ""
      );
      const allTerms = (result.terms || []).map((t) =>
        normalizeText(t.value || "")
      );

      const countryInResult =
        description.includes(countryTerm) ||
        secondaryText.includes(countryTerm) ||
        allTerms.some((t) => t === countryTerm || t.includes(countryTerm));

      if (countryInResult) {
        score += 15;
      }
    }

    // === PRIORITY 4: Result position bonus ===
    // Google Places usually returns the most relevant result first
    // Give a small bonus to earlier results (tiebreaker)
    score += Math.max(0, (5 - index) * 2);

    // === PRIORITY 5: Simplicity bonus ===
    // Prefer simpler results (fewer terms usually means more general/relevant location)
    const termsCount = result.terms?.length || 0;
    if (termsCount <= 3) {
      score += 10;
    } else if (termsCount <= 5) {
      score += 5;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = result;
    }
  });

  return bestMatch;
};

const useMapsFeatures = () => {
  const places = useMapsLibrary("places");
  const map = useMap();

  const [sessionToken, setSessionToken] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [geocoder, setGeocoder] = useState(null);

  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    const geo = new window.google.maps.Geocoder();
    setGeocoder(geo);
  }, [places, map]);

  const getLocationNameByLatLng = async ({ lat, lng, originalTypes }) => {
    const latLng = { lat, lng };

    const response = await geocoder.geocode({ location: latLng });
    const results = response.results || [];

    if (results[0]) {
      const result = results[0];

      const resolvedOriginalTypes =
        originalTypes && originalTypes.length
          ? originalTypes
          : result.types || [];

      const { name, longName } = parseLocationNames(
        result,
        resolvedOriginalTypes
      );

      const geometry = result.geometry || {};
      const location = geometry.location
        ? { lat: geometry.location.lat(), lng: geometry.location.lng() }
        : { lat, lng };
      const viewport = geometry.viewport
        ? {
            south: geometry.viewport.getSouthWest().lat(),
            west: geometry.viewport.getSouthWest().lng(),
            north: geometry.viewport.getNorthEast().lat(),
            east: geometry.viewport.getNorthEast().lng(),
          }
        : null;

      return {
        ok: true,
        // Backward compatibility
        name,
        longName,
        id: result.place_id,
        // Unified metadata
        metadata: {
          place_id: result.place_id,
          types: result.types || [],
          formatted_address: result.formatted_address || longName || name || "",
          address_components: result.address_components || [],
          geometry: {
            location,
            viewport,
          },
        },
      };
    }

    return {
      ok: false,
      name: "",
      longName: "",
      id: null,
      metadata: {
        place_id: null,
        types: [],
        formatted_address: "",
        address_components: [],
        geometry: {
          location: { lat, lng },
          viewport: null,
        },
      },
    };
  };

  const getPredictionsByKeyword = useCallback(
    async (value) => {
      if (!autocompleteService || !value) {
        return [];
      }

      const request = { input: value, sessionToken };

      try {
        const predictions = await new Promise((resolve, reject) => {
          autocompleteService.getPlacePredictions(
            request,
            (predictions, status) => {
              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                predictions
              ) {
                resolve(predictions);
              } else {
                reject();
              }
            }
          );
        });

        return predictions;
      } catch (error) {
        console.error(error);

        return [];
      }
    },
    [autocompleteService, sessionToken]
  );

  // New function to handle coordinate-based search
  const onSearchByCoordinates = async ({
    coordinates,
    onCallbackLoading = () => {},
  }) => {
    try {
      onCallbackLoading(true);

      // Get location name from coordinates
      const locationInfo = await getLocationNameByLatLng(coordinates);

      if (locationInfo) {
        return {
          ok: true,
          data: {
            lat: coordinates.lat,
            lng: coordinates.lng,
            type: "radius",
            radius: 0,
            name: `${locationInfo.name} (${coordinates.lat}, ${coordinates.lng})`,
            longName: `${locationInfo.longName} (${coordinates.lat}, ${coordinates.lng})`,
            id: locationInfo.id || "",
          },
        };
      }

      return { ok: false };
    } catch (error) {
      console.error("Error searching by coordinates:", error);
      return { ok: false, error: error };
    } finally {
      onCallbackLoading(false);
    }
  };

  return {
    sessionToken,
    autocompleteService,
    placesService,
    geocoder,
    getLocationNameByLatLng,
    getPredictionsByKeyword,
    parseLocationNames,
    onSearchByCoordinates,
    getLocationTextByLocationObject,
    getBestLocation,
  };
};

export default useMapsFeatures;
